import { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import axios from "axios";
import { Calendar, ExternalLink, Tag, TrendingUp, Shield, Landmark, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScrollReveal({ children, className, delay = 0 }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={className || ""}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease-out ${delay}ms, transform 0.7s ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const CATEGORY_ICONS = {
  Markets: BarChart3,
  Regulatory: Landmark,
  Tax: TrendingUp,
  Insurance: Shield,
};

const CATEGORY_COLORS = {
  Markets: "#3B82F6",
  Regulatory: "#8B5CF6",
  Tax: "#D4AF37",
  Insurance: "#10B981",
};

export default function NewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios.get(`${API}/news.php`).then((res) => {
      setNews(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric", month: "long", day: "numeric"
    });
  };

  const categories = ["All", ...new Set(news.map((n) => n.category))];
  const filtered = filter === "All" ? news : news.filter((n) => n.category === filter);

  return (
    <div data-testid="news-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#F9FAFB] via-white to-[#EFF6FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-3 block">
              Stay Informed
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-semibold tracking-tight text-[#0B1F3A] mb-4">
              Latest Investment News
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Stay updated with the latest developments in markets, regulations, and financial planning.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Filter + News */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="news-category-filter">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`text-sm px-4 py-2 rounded-full border transition-all ${
                  filter === cat
                    ? "border-[#3B82F6] bg-[#3B82F6]/5 text-[#3B82F6] font-medium"
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
                data-testid={`news-filter-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-gray-100 p-6 animate-pulse flex gap-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-3">
                    <div className="h-4 bg-gray-100 rounded w-1/4" />
                    <div className="h-6 bg-gray-100 rounded w-3/4" />
                    <div className="h-4 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-5">
              {filtered.map((item, i) => {
                const Icon = CATEGORY_ICONS[item.category] || Tag;
                const color = CATEGORY_COLORS[item.category] || "#3B82F6";
                return (
                  <ScrollReveal key={item.id} delay={i * 80}>
                    <div
                      className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12)] hover:-translate-y-0.5 flex gap-5 md:gap-8 items-start"
                      data-testid={`news-item-${i}`}
                    >
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${color}10` }}
                      >
                        <Icon className="w-6 h-6" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <span
                            className="text-xs font-medium px-2.5 py-0.5 rounded-full"
                            style={{ backgroundColor: `${color}10`, color }}
                          >
                            {item.category}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Calendar className="w-3 h-3" />
                            {formatDate(item.created_at)}
                          </span>
                        </div>
                        <h3 className="font-['Outfit'] text-lg font-semibold text-[#0B1F3A] mb-1.5">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed mb-2">
                          {item.excerpt}
                        </p>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> Source: {item.source}
                        </span>
                      </div>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500">No news in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
